import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Checkbox,
  FormControl,
  FormHelperText,
  Link,
  Typography,
} from '@mui/material';

import FacilityAccessChips from '@/components/FacilityAccessChips/FacilityAccessChips';

const buildTreeMetadata = (treeData) => {
  const nodeById = new Map();
  const parentById = new Map();
  const descendantsById = new Map();

  const registerNode = (node, parentId = null) => {
    nodeById.set(node.id, node);
    parentById.set(node.id, parentId);

    (node.children || []).forEach((child) => registerNode(child, node.id));
  };

  const collectDescendants = (node) => {
    const descendants = [];

    (node.children || []).forEach((child) => {
      descendants.push(child.id, ...collectDescendants(child));
    });

    descendantsById.set(node.id, descendants);

    return descendants;
  };

  treeData.forEach((node) => registerNode(node));
  treeData.forEach((node) => collectDescendants(node));

  return {
    nodeById,
    parentById,
    descendantsById,
  };
};

const FacilityGroupTreeSelector = ({
  treeData = [],
  value = [],
  onChange = () => {},
  disabled = false,
  helperText = '',
  organizationId = null,
}) => {
  const navigate = useNavigate();
  const { nodeById, parentById, descendantsById } = useMemo(
    () => buildTreeMetadata(treeData),
    [treeData],
  );

  const selectedIds = useMemo(() => new Set(value), [value]);

  const normalizeSelectedIds = (ids) => {
    const uniqueIds = Array.from(new Set(ids));
    const uniqueIdSet = new Set(uniqueIds);

    return uniqueIds.filter((id) => {
      let parentId = parentById.get(id);

      while (parentId !== null && parentId !== undefined) {
        if (uniqueIdSet.has(parentId)) {
          return false;
        }

        parentId = parentById.get(parentId);
      }

      return true;
    });
  };

  const handleToggle = (groupId, checked) => {
    if (disabled) {
      return;
    }

    if (checked) {
      onChange(normalizeSelectedIds([...value, groupId]));
      return;
    }

    onChange(value.filter((selectedId) => selectedId !== groupId));
  };

  const handleGroupLinkClick = (event, groupId) => {
    event.stopPropagation();

    if (disabled) {
      return;
    }

    if (!organizationId) {
      return;
    }

    navigate(
      `/admin/updateOrganization/${organizationId}/facility-groups/${groupId}`,
    );
  };

  const renderNode = (node, level = 0) => {
    const isChecked = selectedIds.has(node.id);
    const isIndeterminate =
      !isChecked &&
      (descendantsById.get(node.id) || []).some((id) => selectedIds.has(id));

    return (
      <Box
        key={node.id}
        sx={{ ml: level === 0 ? 0 : 3, mt: level === 0 ? 0 : 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <Checkbox
            checked={isChecked}
            indeterminate={isIndeterminate}
            disabled={disabled}
            onChange={(event) => handleToggle(node.id, event.target.checked)}
            size='small'
            sx={{ mt: 0.25 }}
          />

          <Box sx={{ minWidth: 0 }}>
            {organizationId && !disabled ? (
              <Link
                component='button'
                type='button'
                variant='body2'
                onClick={(event) => handleGroupLinkClick(event, node.id)}
                sx={{ display: 'block', textAlign: 'left' }}>
                {node.name}
              </Link>
            ) : (
              <Typography
                variant='body2'
                color={disabled ? 'text.disabled' : 'text.primary'}>
                {node.name}
              </Typography>
            )}
            <Typography
              variant='caption'
              color='text.secondary'
              sx={{ display: 'block' }}>
              {node.facility_count || 0} directly assigned facilities
            </Typography>
          </Box>
        </Box>

        {(node.children || []).length > 0 && (
          <Box
            sx={{
              borderLeft: 1,
              borderColor: 'divider',
              ml: 2.25,
              pl: 0.75,
            }}>
            {node.children.map((child) => renderNode(child, level + 1))}
          </Box>
        )}
      </Box>
    );
  };

  const selectedFacilityGroups = value
    .map((groupId) => nodeById.get(groupId))
    .filter(Boolean)
    .map((group) => ({ id: group.id, name: group.name }));

  return (
    <FormControl error={!!helperText} fullWidth>
      {selectedFacilityGroups.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
            Selected Facility Groups
          </Typography>
          <FacilityAccessChips
            facilities={selectedFacilityGroups}
            hasAllFacilitiesAccess={false}
            visibleCount={3}
          />
        </Box>
      )}

      <Box>{treeData.map((node) => renderNode(node))}</Box>

      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
};

export default FacilityGroupTreeSelector;
